import { useCallback, useEffect } from "react";
import * as Mount from "preview/mount";

// The document the editor's preview pane loads.
//
// It renders no UI at all. Everything it does is a reply to a postMessage from
// the editor in the parent window — see preview/mount, which owns the DOM of
// this document once `mount` arrives.
//
// It is a route of THIS application rather than a separate build, which is the
// one place this port departs from gen-2. There the preview was served from the
// tenant's own domain, because the page being previewed had to run under the
// origin it would be served from.
//
// The frame is therefore SAME-ORIGIN, and a tenant's `<script>` runs in it with
// this app's origin. That is a real difference from the sandboxed iframe on the
// page's own preview screen, and it is the reason this route is not linked from
// anywhere: it is reachable only by the editor, which composes what goes into
// it from the page the signed-in account already owns.
//
// SINCE P142 IT IS LOADED FROM THE SITES HOSTNAME, and that is the whole point:
// the same bundle, the same route, a different origin — so a tenant's `<script>`
// no longer runs with the application's. The editor builds the `src` (see
// `previewSrc` in env) and names itself in `?editor=`, because a document served
// from another origin cannot work out which window is allowed to drive it.
//
// The fallback is this route on the app's own origin, which is what a local
// checkout and an environment with no deployed cdn stack still get. There is no
// second origin to move to there, so the exposure this closes stays open in
// exactly the places that have no tenant traffic.
export const Content = () => {
  const messageHandler = useCallback((event) => {
    // THE EDITOR'S ORIGIN, which since P142 is not this document's — this one is
    // served from the sites hostname precisely so a tenant's `<script>` runs
    // somewhere that is not the application. See `editorOrigin` in preview/mount.
    if (event.origin !== Mount.editorOrigin()) {
      return;
    }

    let msg;
    try {
      msg = JSON.parse(event.data);
    } catch (e) {
      return;
    }

    if (!msg || typeof msg.message !== "string") {
      return;
    }

    if (msg.message === "mount") {
      Mount.mount(msg.payload);
    }

    if (msg.message === "selected") {
      Mount.select(msg.payload);
    }

    if (msg.message === "config") {
      Mount.config(msg.payload);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("message", messageHandler, false);
    return () => window.removeEventListener("message", messageHandler, false);
  }, [messageHandler]);

  // Announced once, after the listener is attached. The editor composes its
  // payload only when this arrives — it needs a round trip to fetch the page's
  // sections, and starting that before the frame can receive the answer is how
  // the first document gets lost.
  useEffect(() => {
    Mount.send("preview.mounted");
  }, []);

  return null;
};
